import axios from "axios";
import { newApiBaseUrl } from "../../axiosInstance";
import * as actionType from "../store/action";
import showAlert from "./ShowAlert";
import Swal from "sweetalert2";
import { setPreload } from "./CartsOpen";
import { markTabAuthToken } from "../auth/tabAuthSync";
import {
  buildVerificationPayload,
  clearEmailVerificationStorage,
  dispatchPendingEmailVerification,
  dismissEmailVerificationFlow,
  isProfileEmailVerified,
  persistPendingVerification,
  setStoredEmailVerified,
  shouldRequireEmailVerification,
  applyDismissedEmailVerificationIfNeeded,
  isEmailVerificationDismissedInStorage,
  isGuestRole,
} from "../utils/emailVerification";

export const resolveStationId = (user = {}) => {
  if (user?.defaultLocation != null && user.defaultLocation !== "") {
    return String(user.defaultLocation);
  }

  if (user?.StationId != null && user.StationId !== "") {
    return String(user.StationId);
  }

  if (user?.stationId != null && user.stationId !== "") {
    return String(user.stationId);
  }

  return null;
};

export const readSessionStationId = () => {
  try {
    const saved = sessionStorage.getItem("stationId");
    if (saved != null && saved !== "") return String(saved);
  } catch {
    /* ignore */
  }
  return null;
};

/** Station from a deep link (pgw-web product URLs send `locationId`). */
export const readRequestedStationIdFromSearch = (search) => {
  const source =
    search != null
      ? search
      : typeof window !== "undefined"
        ? window.location.search
        : "";
  const params = new URLSearchParams(String(source).replace(/^\?/, ""));
  const from =
    params.get("locationId") || params.get("stationId") || params.get("branch");
  const loc = String(from ?? "").trim();
  return loc || null;
};

export const persistRequestedStationIdFromUrl = (search) => {
  const id = readRequestedStationIdFromSearch(search);
  if (!id) return null;
  try {
    sessionStorage.setItem("stationId", id);
  } catch {
    /* ignore */
  }
  return id;
};

/**
 * Fresh login always uses the profile default. Rehydration keeps an in-session
 * override (admin station picker / temp address) instead of snapping back.
 * A station on the URL always wins (warehouse deep links).
 */
export const resolveLoginStationId = ({
  profile,
  isUser,
  isAdmin,
  currentStationId,
  search,
} = {}) => {
  const fromUrl = persistRequestedStationIdFromUrl(search);
  if (fromUrl) return fromUrl;

  const profileStationId = resolveStationId(profile);
  if (isUser) return profileStationId;

  const current =
    currentStationId != null && currentStationId !== ""
      ? String(currentStationId)
      : null;
  if (current && current !== String(profileStationId || "")) {
    return current;
  }

  if (isAdmin) {
    const saved = readSessionStationId();
    if (saved) return saved;
  }

  return profileStationId;
};

export const applyPendingCustomerProfile = (dispatch, profile = {}) => {
  dispatch({
    type: actionType.UPDATE_USER,
    payload: {
      id: profile.id || profile.userId,
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      email: profile.email || profile.username || "",
    },
  });
};

export const applyStationId = (dispatch, stationId) => {
  if (stationId == null || stationId === "") {
    return;
  }

  const resolved = String(stationId);
  try {
    sessionStorage.setItem("stationId", resolved);
  } catch {
    /* ignore */
  }
  dispatch({
    type: actionType.UPDATE_STATION_ID,
    payload: resolved,
  });
};

export const hydrateBranchFromProfile = () => {
  return (dispatch, getState) => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      return Promise.resolve(null);
    }

    return axios
      .get(`${newApiBaseUrl}/users/me/profile`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "X-Tenant": "hamazon",
        },
      })
      .then((response) => {
        const state = getState?.()?.user;
        applyStationId(
          dispatch,
          resolveLoginStationId({
            profile: response.data,
            isUser: false,
            isAdmin: state?.role === true,
            currentStationId: state?.stationId,
          }),
        );
        if (!isGuestRole() && !isProfileEmailVerified(response.data)) {
          applyPendingCustomerProfile(dispatch, response.data);
        }
        return response.data;
      })
      .catch(() => {
        applyStationId(dispatch, getState?.()?.user?.stationId);
        return null;
      });
  };
};

export const LogOut = () => {
  return (dispatch) => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userRole");
    sessionStorage.removeItem("stationId"); // prevent station leaking to next user
    clearEmailVerificationStorage();
    dispatch({ type: actionType.LOGOUT });
  };
};

export const loginPost = (isUser, password) => {
  return (dispatch, getState) => {
    axios
      .get(`${newApiBaseUrl}/users/me/profile`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
          "X-Tenant": "hamazon",
        },
      })
      .then((x) => {
        const roles = JSON.parse(localStorage.getItem("userRole")) || [];
        const isAdmin = roles?.some(
          (role) => role !== "customer" && role !== "guest",
        );
        const emailVerified = isProfileEmailVerified(x.data);
        const stationId = resolveLoginStationId({
          profile: x.data,
          isUser,
          isAdmin,
          currentStationId: getState?.()?.user?.stationId,
        });

        if (
          shouldRequireEmailVerification({
            profile: x.data,
          })
        ) {
          applyStationId(dispatch, stationId);
          applyPendingCustomerProfile(dispatch, x.data);
          dispatchPendingEmailVerification(
            dispatch,
            buildVerificationPayload({
              customerId: x.data.id || x.data.userId,
              email: x.data.email || x.data.username,
              firstName: x.data.firstName,
              lastName: x.data.lastName,
            }),
            {
              resend: !isEmailVerificationDismissedInStorage(),
              resendThunk: resendVerificationEmail,
            },
          );
          applyDismissedEmailVerificationIfNeeded(dispatch);
          return;
        }

        setStoredEmailVerified(true);
        dispatch({
          type: actionType.LOGIN,
          payload: {
            user: x.data,
            role: isAdmin,
            emailVerified,
            stationId,
            ...(isUser === false && { isGuestHydration: true }),
          },
        });
      })

      .catch(() => {
        localStorage.removeItem("jwtToken");
        if (isUser) {
          showAlert({ msgTitle: "שם משתמש וסיסמה שגויים", msgType: "error" });
        }
      });
  };
};
export const sendVerifiedMail = (token) => {
  return (dispatch) => {
    dispatch(setPreload(true));
    const jwtToken = localStorage.getItem("jwtToken");

    if (!jwtToken) {
      showAlert({
        msgTitle: "פג תוקף ההתחברות, יש להתחבר מחדש",
        msgType: "error",
      });
      dispatch(setPreload(false));
      return Promise.reject(new Error("Missing jwtToken"));
    }

    return axios
      .post(
        `${newApiBaseUrl}/users/me/email-verification/verify`,
        { validationCode: token },
        {
          headers: {
            "x-tenant": "hamazon",
            Authorization: `Bearer ${jwtToken}`,
          },
        },
      )
      .then((res) => {
        setStoredEmailVerified(true);
        // We already have a JWT (from registration), so hydrate app user state
        // without showing the login dialog again.
        dispatch(loginPost(false));
        return res;
      })
      .catch((err) => {
        const status = err.response?.status;
        const msgTitle =
          status === 401 || status === 403
            ? "פג תוקף ההתחברות, יש להתחבר מחדש"
            : status === 400
              ? "קוד אימות שגוי"
              : "אירעה שגיאה, נא לנסות שוב";

        showAlert({ msgTitle, msgType: "error" });
        throw err;
      })
      .finally(() => dispatch(setPreload(false)));
  };
};

export const resendVerificationEmail = (options = {}) => {
  return (dispatch) => {
    dispatch(setPreload(true));
    const jwtToken = localStorage.getItem("jwtToken");

    if (!jwtToken) {
      showAlert({
        msgTitle: "פג תוקף ההתחברות, יש להתחבר מחדש",
        msgType: "error",
      });
      dispatch(setPreload(false));
      return Promise.reject(new Error("Missing jwtToken"));
    }

    return axios
      .post(
        `${newApiBaseUrl}/users/me/email-verification/resend`,
        {},
        {
          headers: {
            "x-tenant": "hamazon",
            Authorization: `Bearer ${jwtToken}`,
          },
        },
      )
      .then((res) => {
        if (!options?.suppressAlert) {
          showAlert({
            msgTitle: "נשלח קוד אימות חדש למייל",
            msgType: "success",
          });
        }
        return res;
      })
      .catch((err) => {
        const status = err.response?.status;
        const msgTitle =
          status === 401 || status === 403
            ? "פג תוקף ההתחברות, יש להתחבר מחדש"
            : "אירעה שגיאה בשליחת קוד חדש, נא לנסות שוב";
        showAlert({ msgTitle, msgType: "error" });
        throw err;
      })
      .finally(() => dispatch(setPreload(false)));
  };
};

export const loginToken = (data, isUser, options = {}) => {
  return async (dispatch) => {
    try {
      const awsResponse = await axios.post(
        `${newApiBaseUrl}/auth/login`,
        {
          username: data.userName,
          password: data.password,
        },
        { headers: { "x-tenant": "hamazon" } },
      );

      localStorage.setItem("jwtToken", awsResponse.data.jwt);
      localStorage.setItem(
        "userRole",
        JSON.stringify(awsResponse.data.roles || []),
      );
      markTabAuthToken(awsResponse.data.jwt);
      dispatch({ type: actionType.JWT_UPDATED });

      if (isUser && awsResponse.data.emailVerified === false) {
        try {
          localStorage.setItem("lastLoginEmail", data.userName);
        } catch {
          /* ignore */
        }
        dispatchPendingEmailVerification(
          dispatch,
          buildVerificationPayload({
            customerId: awsResponse.data.userId,
            email: data.userName,
            password: data.password,
          }),
        );
        dispatch(hydrateBranchFromProfile());
        dispatch(resendVerificationEmail({ suppressAlert: true }));
        return awsResponse.data.jwt;
      }

      setStoredEmailVerified(true);
      dispatch(loginPost(isUser, data.password));
      return awsResponse.data.jwt;
    } catch (error) {
      const errorMessage =
        error.response?.status === 401 ? "שם משתמש וסיסמה שגויים" : "שגיאה";
      if (!options?.suppressAlert) {
        showAlert({ msgTitle: errorMessage, msgType: "error" });
      }
      const err = new Error(errorMessage);
      err.cause = error;
      throw err;
    }
  };
};

export const customerRegister = (val, options = {}) => {
  return (dispatch) => {
    dispatch(setPreload(true));
    return axios
      .post(
        `${newApiBaseUrl}/users/customer`,
        (() => {
          const defaultLocation = val.defaultLocation || val.StationId;
          const body = {
            email: val.email,
            password: val.password,
            firstName: val.firstName,
            lastName: val.lastName,
            phone: val.phone,
            username: val.email,
            tenant: "hamazon",
            addrCity: val.addrCity,
            addrStreet: val.addrStreet,
            addrHouse: val.addrHouse,
            addrFloor: val.addrFloor,
            addrApartment: val.addrApartment,
            addrZipCode: val.addrZipCode,
          };
          if (defaultLocation != null && defaultLocation !== "") {
            body.defaultLocation = String(defaultLocation);
          }
          const orderRecipientName = String(
            val.addrOrderRecipientName ?? "",
          ).trim();
          if (orderRecipientName) {
            body.addrOrderRecipientName = orderRecipientName;
          }
          return body;
        })(),
        { headers: { "x-tenant": "hamazon" } },
      )
      .then((x) => {
        if (x.data?.jwt) {
          localStorage.setItem("jwtToken", x.data.jwt);
          markTabAuthToken(x.data.jwt);
        }
        if (x.data?.roles) {
          localStorage.setItem("userRole", JSON.stringify(x.data.roles));
        } else {
          // Prevent fallback guest UI when backend omits roles in register response.
          localStorage.setItem("userRole", JSON.stringify(["customer"]));
        }
        dispatchPendingEmailVerification(
          dispatch,
          buildVerificationPayload({
            customerId: x.data.userId,
            email: val.email,
            password: val.password,
            passwordMail: x.data.Name,
          }),
        );
        if (!options?.suppressAlert) {
          showAlert({
            msgTitle: "נרשמת בהצלחה! קוד אימות נשלח למייל שלך",
            msgType: "success",
          });
        }
        return x;
      })
      .catch((err) => {
        const isEmailConflict = err.response && err.response.status === 409;
        const msgTitle = isEmailConflict
          ? "כתובת המייל כבר קיימת במערכת"
          : err.response?.data?.Message || "שגיאה ברישום";

        if (!options?.suppressAlert) {
          if (isEmailConflict) {
            // Show prominent popup for email conflict
            Swal.fire({
              icon: "error",
              title: msgTitle,
              text: "אנא בחר בכתובת מייל אחרת",
              confirmButtonText: "בחזרה לרישום",
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                confirmButton: "hz-swal-confirm",
                icon: "hz-swal-icon",
              },
              didOpen: () => {
                document.body.classList.add("hz-alert-open");
              },
              willClose: () => {
                document.body.classList.remove("hz-alert-open");
              },
            });
          } else {
            showAlert({ msgTitle, msgType: "error" });
          }
        }
        return Promise.reject(new Error(msgTitle));
      })
      .finally(() => dispatch(setPreload(false)));
  };
};

export const sendPasswordPgw = async (email, options = {}) => {
  try {
    const res = await axios.post(
      `${newApiBaseUrl}/auth/password-reset/request`,
      { email: email, tenant: "hamazon" },
      { headers: { "x-tenant": "hamazon" } },
    );
    if (!options?.suppressAlert) {
      showAlert({ msgTitle: "נשלח לך מייל", msgType: "success" });
    }
    return res.data;
  } catch (err) {
    if (!options?.suppressAlert) {
      showAlert({
        msgTitle: "לא מצאנו את כתובת המייל במערכת",
        msgType: "error",
      });
    }
    throw err;
  }
};

export const setUserEntry = (value) => {
  return (dispatch) =>
    dispatch({ type: actionType.USER_ENTRY, payload: value });
};

export const dismissEmailVerification = () => {
  return (dispatch) => dismissEmailVerificationFlow(dispatch);
};

export const updateStationId = (value) => {
  return (dispatch) =>
    dispatch({ type: actionType.UPDATE_STATION_ID, payload: value });
};

export const getAllCustomer = (stationId) => {
  return axios.get(`${newApiBaseUrl}/users`, {
    params: { stationId },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.jwtToken}`,
      "X-Tenant": "hamazon",
    },
  });
};

/**
 * Whitelist profile fields the PGW API accepts (same shape as customerRegister).
 * Strips legacy/extra user props such as username, roles, StationId.
 * addrOrderRecipientName is sent when present on source (including "") so clears persist.
 */
export const buildProfileUpdatePayload = (source = {}) => {
  const str = (v) => String(v ?? "");
  const payload = {
    defaultLocation: str(
      source.defaultLocation ?? source.StationId ?? source.stationId ?? "",
    ),
    firstName: str(source.firstName),
    lastName: str(source.lastName),
    phone: str(source.phone),
    addrCity: str(source.addrCity ?? source.City ?? ""),
    addrStreet: str(source.addrStreet),
    addrHouse: str(source.addrHouse),
    addrFloor: str(source.addrFloor),
    addrApartment: str(source.addrApartment),
    addrZipCode: str(source.addrZipCode),
  };
  if (Object.prototype.hasOwnProperty.call(source, "addrOrderRecipientName")) {
    payload.addrOrderRecipientName = str(source.addrOrderRecipientName).trim();
  }
  // Opt-in only — never send email on ordinary profile/address updates.
  if (source.includeEmail && source.email) {
    payload.email = str(source.email).trim().toLowerCase();
  }
  return payload;
};

export const UpdateUser = (updatedData) => {
  const payload = buildProfileUpdatePayload(updatedData);
  return axios.put(`${newApiBaseUrl}/users/me/profile`, payload, {
    headers: {
      "Content-Type": "application/json",
      "x-tenant": "hamazon",
      Authorization: `bearer ${localStorage.jwtToken}`,
    },
  });
};

/** Update the pending verification email on the profile, persist it, then resend the code. */
export const updateVerificationEmail = (email) => {
  return (dispatch, getState) => {
    const normalized = String(email || "")
      .trim()
      .toLowerCase();
    if (!normalized) {
      return Promise.reject(new Error("Missing email"));
    }

    const state = getState();
    const user = state.user?.user || {};
    const pending = state.user?.userHaveReset || {};

    dispatch(setPreload(true));
    return UpdateUser({
      ...user,
      firstName: user.firstName || pending.firstName || "",
      lastName: user.lastName || pending.lastName || "",
      phone: user.phone || "",
      email: normalized,
      includeEmail: true,
    })
      .then(() => {
        const next = buildVerificationPayload({
          customerId: pending.customerId || user.id,
          email: normalized,
          password: pending.password,
          passwordMail: pending.passwordMail,
          firstName: pending.firstName || user.firstName,
          lastName: pending.lastName || user.lastName,
        });
        persistPendingVerification(next);
        try {
          localStorage.setItem("lastLoginEmail", normalized);
        } catch {
          /* ignore */
        }
        dispatch({
          type: actionType.VERIFIED_EMAIL,
          payload: next,
        });
        dispatch(setPreload(false));
        return dispatch(resendVerificationEmail());
      })
      .catch((err) => {
        dispatch(setPreload(false));
        showAlert({
          msgTitle: "לא הצלחנו לעדכן את כתובת המייל",
          msgType: "error",
        });
        throw err;
      });
  };
};

export const sendPasswordVerify = async (
  validationCode,
  userId,
  options = {},
) => {
  try {
    const res = await axios.post(
      `${newApiBaseUrl}/auth/password-reset/verify`,
      { validationCode, userId },
      {
        headers: {
          "x-tenant": "hamazon",
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  } catch (err) {
    if (!options?.suppressAlert) {
      showAlert({ msgTitle: "קוד האיפוס אינו תקין", msgType: "error" });
    }
    throw err;
  }
};

export const confirmPasswordReset = async (
  userId,
  validationCode,
  newPassword,
  options = {},
) => {
  try {
    const res = await axios.post(
      `${newApiBaseUrl}/auth/password-reset/confirm`,
      {
        userId,
        validationCode,
        newPassword,
      },
      {
        headers: {
          "x-tenant": "hamazon",
          "Content-Type": "application/json",
        },
      },
    );

    return res.data;
  } catch (err) {
    if (!options?.suppressAlert) {
      showAlert({ msgTitle: "משהו השתבש", msgType: "error" });
    }
    throw err;
  }
};
